// Navigation chrome: mobile app bar / top tabs / bottom navigation / next bar,
// desktop top bar / section row / pager bar, and the shared legal notice.
import React, { useEffect, useRef } from 'react';
import { NAV_ICONS, chevronLeftWhite, iconDownloadWhite, iconArrowRightWhite, iconInfoTeal, iconDownloadSmallWhite } from '../icons';
import { LEGAL_NOTICE_TEXT } from '../hfpDisplay';

// ---------------------------------------------------------------------------
// Mobile
// ---------------------------------------------------------------------------

/** Plum app bar: Back · Aa (reading options) · Download (behind the PDF flag). */
export function MobileAppBar({ onBack, onReadingOptions, onDownload, showDownload }) {
  return (
    <div className="flex items-center justify-between h-[56px] pl-[4px] pr-[8px] shrink-0 bg-[var(--hfp-ink-heading)]">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-[2px] h-[44px] pr-[12px] rounded-[var(--hfp-radius-8)]"
      >
        <img src={chevronLeftWhite} alt="" aria-hidden="true" width={24} height={24} />
        <span className="hfp-body-16 text-[color:var(--hfp-ink-on-plum)]">Back</span>
      </button>
      <div className="flex items-start">
        <button
          type="button"
          onClick={onReadingOptions}
          aria-label="Reading options"
          className="flex items-center justify-center size-[44px] rounded-[var(--hfp-radius-8)] hfp-sub-16 text-[color:var(--hfp-ink-on-plum)]"
        >
          Aa
        </button>
        {showDownload && (
          <button
            type="button"
            onClick={onDownload}
            aria-label="Download PDF"
            className="flex items-center justify-center size-[44px] rounded-[var(--hfp-radius-8)]"
          >
            <img src={iconDownloadWhite} alt="" aria-hidden="true" width={24} height={24} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Tab strip for list-level pages; the current tab is kept in view. */
export function MobileTopTabs({ tabs, currentKey, onSelect }) {
  const currentRef = useRef(null);
  useEffect(() => {
    // Keep the current tab in view (pre-scrolled per the design). jsdom has
    // no scrollIntoView, so guard for tests.
    currentRef.current?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [currentKey]);
  return (
    <nav aria-label="Report pages" className="relative h-[48px] shrink-0 bg-[var(--hfp-surface-card)]">
      <div className="hfp-tab-strip flex gap-[4px] items-start px-[16px] h-[47px]">
        {tabs.map((tab) => {
          const isCurrent = tab.key === currentKey;
          return (
            <button
              key={tab.key}
              type="button"
              aria-current={isCurrent ? 'page' : undefined}
              ref={isCurrent ? currentRef : undefined}
              onClick={() => onSelect(tab.key)}
              className="relative flex flex-col items-center justify-center h-[47px] px-[12px] rounded-[var(--hfp-radius-8)] shrink-0"
            >
              <span className={`hfp-label-14 whitespace-nowrap ${isCurrent ? 'text-[color:var(--hfp-ink-heading)]' : 'text-[color:var(--hfp-ink-mid)]'}`}>
                {tab.label}
              </span>
              {isCurrent && <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--hfp-ink-heading)]" />}
            </button>
          );
        })}
      </div>
      <div aria-hidden="true" className="absolute top-0 bottom-[1px] left-0 w-[32px] pointer-events-none bg-gradient-to-r from-white to-transparent" />
      <div aria-hidden="true" className="absolute top-0 bottom-[1px] right-0 w-[32px] pointer-events-none bg-gradient-to-l from-white to-transparent" />
      <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-px bg-[var(--hfp-line-hair)]" />
    </nav>
  );
}

/**
 * Sticky bar above the bottom navigation holding the one green button.
 * Label = destination, never the word "Next". A slot with nothing to do is
 * absent, never greyed — render nothing when there is no `label`.
 */
export function MobileNextBar({ label, onNext, download = false }) {
  if (!label) return null;
  return (
    <div className="flex items-start h-[64px] px-[16px] py-[6px] shrink-0 bg-[var(--hfp-surface-card)]">
      <button
        type="button"
        onClick={onNext}
        className="relative flex flex-1 min-w-0 items-center justify-center gap-[8px] h-[52px] px-[16px] rounded-[var(--hfp-radius-14)] bg-[var(--hfp-action-next-green)] hover:bg-[var(--hfp-action-next-green-hover)] active:bg-[var(--hfp-action-next-green-pressed)]"
      >
        <span className="hfp-label-14 text-center text-[color:var(--hfp-ink-on-plum)] whitespace-nowrap">{label}</span>
        <img
          src={download ? iconDownloadSmallWhite : iconArrowRightWhite}
          alt=""
          aria-hidden="true"
          width={download ? 20 : 24}
          height={download ? 20 : 24}
          className={download ? '' : 'absolute right-[14px]'}
        />
      </button>
    </div>
  );
}

const NAV_PARTS = [
  { key: 'guide', label: 'How to use it' },
  { key: 'profile', label: 'The profile' },
  { key: 'help', label: 'What could help' },
];

/** Three parts, always visible. Current shows the tinted icon slot. */
export function MobileBottomNav({ currentPart, onSelect }) {
  return (
    <nav aria-label="Report" className="relative flex items-start h-[64px] shrink-0 bg-[var(--hfp-surface-card)]">
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-[var(--hfp-line-hair)]" />
      {NAV_PARTS.map((part) => {
        const isCurrent = part.key === currentPart;
        return (
          <button
            key={part.key}
            type="button"
            aria-current={isCurrent ? 'page' : undefined}
            onClick={() => onSelect(part.key)}
            className="flex flex-1 min-w-0 flex-col gap-[2px] h-[64px] items-center justify-center pt-[8px] pb-[6px]"
          >
            <span
              className="flex h-[32px] w-[64px] items-center justify-center rounded-[var(--hfp-radius-16)]"
              style={isCurrent ? { background: 'var(--hfp-surface-head-plum)' } : undefined}
            >
              <img src={NAV_ICONS[part.key]} alt="" aria-hidden="true" width={24} height={24} />
            </span>
            <span className={`hfp-label-14 text-center whitespace-nowrap ${isCurrent ? 'text-[color:var(--hfp-ink-heading)]' : 'text-[color:var(--hfp-ink-mid)]'}`}>
              {part.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Desktop (1720 and Air 1280 — bars fill, content column is 1080)
// ---------------------------------------------------------------------------

/** Plum top bar: back to the record · lockup · segmented part switcher · Download PDF. */
export function DesktopTopBar({ childName, currentPart, onSelectPart, onBack, onDownload, showDownload }) {
  return (
    <div className="relative flex items-center gap-[16px] h-[80px] px-[24px] shrink-0 bg-[var(--hfp-ink-heading)]">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center justify-center gap-[6px] h-[44px] pl-[12px] pr-[14px] rounded-[var(--hfp-radius-16)] border-[1.5px] border-solid border-[color:var(--hfp-nav-on-bar-line)]"
      >
        <img src={chevronLeftWhite} alt="" aria-hidden="true" width={15} height={15} />
        <span className="hfp-label-14 text-[color:var(--hfp-ink-on-plum)]">Back</span>
      </button>
      <span aria-hidden="true" className="h-[28px] w-px bg-[var(--hfp-nav-on-bar-line)]" />
      <div className="flex flex-col gap-px">
        <span className="hfp-label-14 !text-[12px] tracking-[1.32px] text-[color:var(--hfp-nav-on-bar-soft)] whitespace-nowrap">
          STRENGTHS AND NEEDS PROFILE
        </span>
        <span className="hfp-sub-16 text-[color:var(--hfp-ink-on-plum)] whitespace-nowrap">{childName}</span>
      </div>
      <div className="flex-1" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-[4px] p-[4px] rounded-[var(--hfp-radius-8)] bg-[var(--hfp-nav-segment-ground)]">
        {NAV_PARTS.map((part, i) => {
          const isCurrent = part.key === currentPart;
          return (
            <button
              key={part.key}
              type="button"
              aria-current={isCurrent ? 'page' : undefined}
              onClick={() => onSelectPart(part.key)}
              className={`flex items-center justify-center gap-[8px] h-[40px] px-[16px] rounded-[var(--hfp-radius-8)] ${isCurrent ? 'bg-[var(--hfp-surface-card)]' : 'bg-[var(--hfp-nav-segment-ground)]'}`}
            >
              <span
                className="flex items-center justify-center size-[20px] rounded-[var(--hfp-radius-pill)]"
                style={{ background: isCurrent ? 'var(--hfp-ink-heading)' : 'var(--hfp-nav-on-bar-line)' }}
              >
                <span className="hfp-label-14 !text-[12px] !leading-[16px] text-[color:var(--hfp-ink-on-plum)]">{i + 1}</span>
              </span>
              <span className={`whitespace-nowrap ${isCurrent ? 'hfp-label-14 text-[color:var(--hfp-ink-heading)]' : 'hfp-label-14 !font-normal text-[color:var(--hfp-ink-on-plum-muted)]'}`}>
                {part.label}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-1 items-center justify-end gap-[12px]">
        {showDownload && (
          <button
            type="button"
            onClick={onDownload}
            className="flex items-center justify-center gap-[8px] h-[44px] pl-[14px] pr-[16px] rounded-[var(--hfp-radius-8)] border border-solid border-[color:var(--hfp-surface-card)]"
          >
            <img src={iconDownloadWhite} alt="" aria-hidden="true" width={16} height={16} />
            <span className="hfp-label-14 text-[color:var(--hfp-ink-on-plum)] whitespace-nowrap">Download PDF</span>
          </button>
        )}
      </div>
    </div>
  );
}

/** Section switcher under the top bar, with the position badge on section pages. */
export function DesktopSectionRow({ chips, currentKey, onSelect, positionLabel }) {
  return (
    <div className="relative flex items-center h-[64px] px-[24px] shrink-0 bg-[var(--hfp-surface-card)] border-b border-solid border-[color:var(--hfp-line-hair)]">
      <div className="flex-1" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-[12px]">
        {chips.map((chip) => {
          const isCurrent = chip.key === currentKey;
          return (
            <button
              key={chip.key}
              type="button"
              aria-current={isCurrent ? 'page' : undefined}
              onClick={() => onSelect(chip.key)}
              className={`flex h-[40px] items-center justify-center px-[8px] rounded-[var(--hfp-radius-8)] ${isCurrent ? 'bg-[var(--hfp-ink-heading)]' : ''}`}
            >
              <span className={`hfp-label-14 whitespace-nowrap ${isCurrent ? 'text-[color:var(--hfp-ink-on-plum)]' : '!font-normal text-[color:var(--hfp-ink-mid)]'}`}>
                {chip.label}
              </span>
            </button>
          );
        })}
      </div>
      {positionLabel && (
        <span className="flex items-center justify-center px-[12px] py-[6px] rounded-[var(--hfp-radius-8)] border border-solid border-[color:var(--hfp-line-border)] bg-[var(--hfp-ground-page)]">
          <span className="hfp-label-14 text-[color:var(--hfp-ink-strong)] whitespace-nowrap">{positionLabel}</span>
        </span>
      )}
    </div>
  );
}

export function PagerButton({ kind, label, onClick, disabled = false }) {
  const isNext = kind === 'next';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex h-[44px] items-center justify-center px-[16px] rounded-[var(--hfp-radius-8)] border border-solid border-[color:var(--hfp-line-border)] ${
        isNext && !disabled
          ? 'bg-[var(--hfp-action-next-green)] hover:bg-[var(--hfp-action-next-green-hover)] active:bg-[var(--hfp-action-next-green-pressed)]'
          : 'bg-[var(--hfp-surface-card)]'
      } ${disabled ? 'opacity-45' : ''}`}
    >
      <span className={`hfp-label-14 whitespace-nowrap ${isNext && !disabled ? 'text-[color:var(--hfp-ink-on-plum)]' : 'text-[color:var(--hfp-ink-mid)]'}`}>
        {label}
      </span>
    </button>
  );
}

/** Sticky foot pager: Previous names the page before, Next names the page after. */
export function DesktopPagerBar({ previous, next, onPrevious, onNext }) {
  return (
    <div className="flex items-center justify-center h-[72px] shrink-0 bg-[var(--hfp-surface-card)] border-t border-solid border-[color:var(--hfp-line-hair)]">
      <div className="flex h-full items-center gap-[12px] w-full max-w-[1080px] px-[24px] xl:px-0">
        {previous ? <PagerButton kind="previous" label={`‹ ${previous.label}`} onClick={onPrevious} /> : <span />}
        <div className="flex-1" />
        {next ? (
          <PagerButton kind="next" label={`Next: ${next.label} ›`} onClick={onNext} />
        ) : (
          <PagerButton kind="next" label="Next ›" disabled />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/** Not-a-medical-assessment line, at the foot of every page and flyout. */
export function LegalNotice({ calibrationNote = null }) {
  return (
    <div className="flex items-start gap-[12px] w-full px-[16px] py-[14px] rounded-[var(--hfp-radius-8)] border border-solid border-[color:var(--hfp-line-info-teal)] bg-[var(--hfp-surface-info-teal)]">
      <img src={iconInfoTeal} alt="" aria-hidden="true" width={18} height={18} className="mt-[1px]" />
      <div className="flex flex-1 min-w-0 flex-col gap-[6px]">
        <p className="hfp-meta-14 w-full text-[color:var(--hfp-ink-mid)]">{LEGAL_NOTICE_TEXT}</p>
        {/* The calibration line renders verbatim from the adapter constant
            (TD-210), exactly as the previous report's footer did. */}
        {calibrationNote && (
          <p className="hfp-meta-14 w-full text-[color:var(--hfp-ink-mid)]">{calibrationNote}</p>
        )}
      </div>
    </div>
  );
}

/** Page head: eyebrow (mobile position line) · H1 · lede. */
export function PageHead({ eyebrow, title, lede, desktop = false }) {
  if (desktop) {
    return (
      <div className="flex items-center gap-[16px] w-full p-[24px] rounded-[var(--hfp-radius-16)] bg-[var(--hfp-surface-head-plum)]">
        <div className="flex flex-1 min-w-0 flex-col">
          <h1 className="hfp-display-28">{title}</h1>
          {lede && <p className="hfp-body-16 mt-[4px] text-[color:var(--hfp-ink-soft)]">{lede}</p>}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-[8px] w-full">
      {eyebrow && <p className="hfp-meta-14 text-[color:var(--hfp-ink-mid)]">{eyebrow}</p>}
      <h1 className="hfp-display-24">{title}</h1>
      {lede && <p className="hfp-body-16 text-[color:var(--hfp-ink-mid)]">{lede}</p>}
    </div>
  );
}
